<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";

import Plyr from "plyr";

import IconClose from "@shared/ui/icons/IconClose.vue";

import { registerStream } from "@features/file-transfer/streamRegistry";
import { useMediaSource } from "@features/file-transfer/useMediaSource";

import type { FileAttachment } from "shared";

import "plyr/dist/plyr.css";

// Модалка проигрывателя видео. Источник — по приоритету:
// 1) стриминговый URL через Service Worker (Range + расшифровка на лету,
//    память константная, старт мгновенный);
// 2) фолбэк — полный блоб через useMediaSource (SW недоступен/легаси-формат).
const props = defineProps<{ file: FileAttachment; visible: boolean }>();
const emit = defineEmits<{ close: [] }>();

const { get: getMedia, load: loadMedia } = useMediaSource();
const media = getMedia(props.file);

// Итоговый src для <video>: стриминговый URL или objectURL блоба.
const streamUrl = ref<string | null>(null);
const src = computed(() => streamUrl.value ?? media.url);

// Контейнер плеера (и заглушки) сразу получает конечные габариты из метаданных:
// вписываем аспект в вьюпорт (960×(100vh−64) макс). Иначе до загрузки метаданных
// <video> контейнер — маленький чёрный квадрат, и анимация открытия играет на
// нём, а рост до полного размера идёт скачком.
const playerBox = computed(() => {
    const { width, height } = props.file;
    if (!width || !height) {
        return undefined;
    }
    return {
        aspectRatio: `${width} / ${height}`,
        height: `min(calc(100vh - 64px), calc(min(960px, 100vw - 64px) * ${(
            height / width
        ).toFixed(4)}))`,
    };
});

const videoEl = ref<HTMLVideoElement | null>(null);
let plyr: Plyr | null = null;

// Первые полсекунды после открытия контролы скрыты (не мигают поверх кадра
// при старте autoplay) — класс снимается по таймеру.
const controlsReady = ref(false);
let controlsTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleControls(): void {
    controlsReady.value = false;
    if (controlsTimer) {
        clearTimeout(controlsTimer);
    }
    controlsTimer = setTimeout(() => {
        controlsReady.value = true;
    }, 500);
}

// Plyr рендерит контролы плоским списком — сгруппировать их чистым CSS в два
// ряда с точным центрированием нельзя (грид связывал бы колонки рядов, флекс
// центрирует между блоками разной ширины). Поэтому после ready переносим узлы
// в свои обёртки-ряды (живые ссылки, слушатели сохраняются): ряд 1 — грид
// `1fr auto 1fr` (play математически по центру), ряд 2 — время/дорожка/итог.
function regroupControls(container: HTMLElement): void {
    const controls = container.querySelector(".plyr__controls");
    if (!controls) {
        return;
    }
    // Идемпотентно: обёртки создаём один раз, а элементы доносим при каждом
    // вызове — часть контролов (меню настроек) Plyr дособирает позже.
    let row1 = controls.querySelector(".mc-vp__row1");
    let left = controls.querySelector(".mc-vp__row1-side_left");
    let right = controls.querySelector(".mc-vp__row1-side_right");
    let row2 = controls.querySelector(".mc-vp__row2");
    if (!row1 || !left || !right || !row2) {
        row1 = document.createElement("div");
        row1.className = "mc-vp__row1";
        left = document.createElement("div");
        left.className = "mc-vp__row1-side mc-vp__row1-side_left";
        right = document.createElement("div");
        right.className = "mc-vp__row1-side mc-vp__row1-side_right";
        row2 = document.createElement("div");
        row2.className = "mc-vp__row2";
        row1.append(left, right);
        controls.append(row1, row2);
    }

    // Переносим только то, что ещё лежит напрямую в .plyr__controls.
    const stray = (sel: string) => controls.querySelector(`:scope > ${sel}`);

    const volume = stray(".plyr__volume");
    if (volume) {
        left.append(volume);
    }
    const play = stray('[data-plyr="play"]');
    if (play) {
        row1.insertBefore(play, right);
    }
    for (const sel of [
        ".plyr__menu",
        '[data-plyr="pip"]',
        '[data-plyr="fullscreen"]',
    ]) {
        const el = stray(sel);
        if (el) {
            right.append(el);
        }
    }
    for (const sel of [
        ".plyr__time--current",
        ".plyr__progress__container",
        ".plyr__time--duration",
    ]) {
        const el = stray(sel);
        if (el) {
            row2.append(el);
        }
    }
}

async function resolveSource(): Promise<void> {
    if (src.value) {
        return;
    }
    streamUrl.value = await registerStream(props.file);
    if (!streamUrl.value) {
        void loadMedia(props.file);
    }
}

// Стрим-источник не заиграл (платформа без стриминговых ответов SW, битый SW
// и т.п.) — молча откатываемся на блоб-путь: пересоздаём плеер с objectURL.
function onVideoError(): void {
    if (!streamUrl.value) {
        return;
    }
    streamUrl.value = null;
    plyr?.destroy();
    plyr = null;
    void loadMedia(props.file);
}

// Plyr поверх нативного <video>: создаём, когда модалка открыта и src готов;
// уничтожаем при закрытии (иначе играл бы за кадром).
watch(
    () => [props.visible, src.value] as const,
    async ([visible, source]) => {
        if (visible && !source) {
            void resolveSource();
        }
        if (visible && source) {
            await nextTick();
            if (videoEl.value && !plyr) {
                plyr = new Plyr(videoEl.value, {
                    // Спрайт из public/ (копия из пакета): по умолчанию Plyr
                    // тянет его с cdn.plyr.io — наш CSP внешние запросы режет.
                    iconUrl: "/plyr.svg",
                    // Пустышка для destroy() (сброс src, обрыв загрузки) — тоже
                    // по умолчанию с cdn.plyr.io; пустая строка делает то же.
                    blankVideo: "",
                    autoplay: true,
                    controls: [
                        "play",
                        "progress",
                        "current-time",
                        "duration",
                        "mute",
                        "volume",
                        "settings",
                        "fullscreen",
                    ],
                    settings: ["speed"],
                    speed: { selected: 1, options: [0.5, 1, 1.5, 2] },
                });
                const host = videoEl.value.closest(".mc-vp__player");
                if (host instanceof HTMLElement) {
                    // Разметка обычно готова сразу, но ready страхует.
                    regroupControls(host);
                    plyr.on("ready", () => regroupControls(host));
                }
                scheduleControls();
            }
        } else if (plyr && !visible) {
            // Звук глушим сразу, а разбор плеера — после leave-анимации
            // (иначе кадр исчезал бы до того, как модалка успела погаснуть).
            plyr.pause();
        }
    },
    { immediate: true }
);

// Разбор плеера по завершении анимации закрытия.
function teardown(): void {
    plyr?.destroy();
    plyr = null;
    controlsReady.value = false;
}

// Esc закрывает модалку (слушатель живёт только пока она открыта).
function onKeydown(e: KeyboardEvent): void {
    if (e.key === "Escape") {
        emit("close");
    }
}

watch(
    () => props.visible,
    (visible) => {
        if (visible) {
            document.addEventListener("keydown", onKeydown);
        } else {
            document.removeEventListener("keydown", onKeydown);
        }
    },
    { immediate: true }
);

onBeforeUnmount(() => {
    plyr?.destroy();
    plyr = null;
    if (controlsTimer) {
        clearTimeout(controlsTimer);
    }
    document.removeEventListener("keydown", onKeydown);
});
</script>

<template>
    <Teleport to="body">
        <Transition name="mc-vp" @after-leave="teardown">
            <div v-if="visible" class="mc-vp" @click="emit('close')">
                <button
                    class="mc-vp__close"
                    type="button"
                    aria-label="Закрыть"
                    @click.stop="emit('close')"
                >
                    <IconClose />
                </button>
                <div
                    v-if="src"
                    class="mc-vp__player"
                    :class="{ 'mc-vp__player_warmup': !controlsReady }"
                    :style="playerBox"
                    @click.stop
                >
                    <video
                        ref="videoEl"
                        :src="src"
                        playsinline
                        @error="onVideoError"
                    />
                </div>
                <!-- Пока источник не готов: постер в габаритах будущего плеера -->
                <div v-else class="mc-vp__wait" :style="playerBox" @click.stop>
                    <img
                        v-if="file.poster"
                        :src="file.poster"
                        :alt="file.name"
                        class="mc-vp__wait-img"
                    />
                    <span class="mc-vp__progress">
                        <template v-if="media.error"
                            >Не удалось загрузить</template
                        >
                        <template v-else>
                            {{ Math.round(media.progress * 100) }}%
                        </template>
                    </span>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.mc-vp {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(4px);
}

/* Габариты контейнера заданы инлайном (playerBox: аспект из метаданных,
   вписан в вьюпорт) — видео заполняет его, скачков размера нет. */
.mc-vp__player {
    max-width: min(960px, 100%);
    max-height: 100%;
    cursor: default;
    --plyr-color-main: var(--mc-acid);
    /* Моно-шрифт и острые углы во всём плеере (кнопки, тултипы, меню). */
    --plyr-font-family: var(--mc-mono);
    --plyr-control-radius: 0;
    /* Ползунки дорожек компактнее дефолтных 13px. */
    --plyr-range-thumb-height: 9px;
}

.mc-vp__player :deep(.plyr) {
    height: 100%;
}

.mc-vp__player :deep(.plyr video) {
    display: block;
    width: 100%;
    height: 100%;
    max-height: calc(100vh - 64px);
    object-fit: contain;
    background: #000;
}

/* Добиваем скругления, которые Plyr задаёт не через var: меню, тултип,
   ползунки range (webkit/moz). */
.mc-vp__player :deep(.plyr__menu__container),
.mc-vp__player :deep(.plyr__tooltip) {
    border-radius: 0;
}

.mc-vp__player :deep(.plyr--full-ui input[type="range"])::-webkit-slider-thumb {
    border-radius: 0;
}

.mc-vp__player :deep(.plyr--full-ui input[type="range"])::-moz-range-thumb {
    border-radius: 0;
}

.mc-vp__player
    :deep(.plyr--full-ui input[type="range"])::-webkit-slider-runnable-track {
    border-radius: 0;
}

.mc-vp__player :deep(.plyr--full-ui input[type="range"])::-moz-range-track {
    border-radius: 0;
}

.mc-vp__player :deep(.plyr__progress__buffer) {
    border-radius: 0;
}

/* ── Контролы полупрозрачной плашкой ПОВЕРХ видео.
   Ряд 1: громкость · play по центру · настройки/PiP/fullscreen.
   Ряд 2: текущее время · прогресс во всю ширину · длительность. */
.mc-vp__player :deep(.plyr--video .plyr__controls) {
    position: absolute;
    /* Плашка уже кадра: до 480px, по центру. */
    left: 50%;
    transform: translateX(-50%);
    bottom: 16px;
    width: min(480px, calc(100% - 32px));
    display: block;
    padding: 8px 12px 10px;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(4px);
    transition:
        opacity 0.2s ease,
        transform 0.2s ease;
}

/* Автоскрытие Plyr со своим transform (родное translateY(100%) затёрло бы
   наше центрирование по X). */
.mc-vp__player :deep(.plyr--video.plyr--hide-controls .plyr__controls) {
    opacity: 0;
    pointer-events: none;
    transform: translateX(-50%) translateY(8px);
}

/* Первые полсекунды после открытия контролы скрыты (см. scheduleControls). */
.mc-vp__player_warmup :deep(.plyr--video .plyr__controls) {
    opacity: 0;
    pointer-events: none;
}

/* Кнопки с компактным паддингом, без заливки: при наведении иконка — acid. */
.mc-vp__player :deep(.plyr__controls .plyr__control) {
    padding: 4px;
}

.mc-vp__player :deep(.plyr__controls .plyr__control:hover),
.mc-vp__player :deep(.plyr__controls .plyr__control[aria-expanded="true"]) {
    background: transparent;
    color: var(--mc-acid);
}

/* Ряд 1: грид 1fr·auto·1fr — боковые зоны равны, play строго по центру. */
.mc-vp__player :deep(.mc-vp__row1) {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
}

/* Глушим плееровские отступы (:first-child { margin-right: auto } и др.) —
   внутри наших обёрток они ломают раскладку (шестерёнка улетала от группы). */
.mc-vp__player :deep(.mc-vp__row1 .plyr__controls__item),
.mc-vp__player :deep(.mc-vp__row2 .plyr__controls__item) {
    margin: 0;
}

.mc-vp__player :deep(.mc-vp__row1-side_right) {
    gap: 8px;
}

.mc-vp__player :deep(.mc-vp__row1-side) {
    display: flex;
    align-items: center;
}

.mc-vp__player :deep(.mc-vp__row1-side_left) {
    justify-content: flex-start;
}

.mc-vp__player :deep(.mc-vp__row1-side_right) {
    justify-content: flex-end;
}

/* Ряд 2: время · дорожка на всю ширину · длительность. */
.mc-vp__player :deep(.mc-vp__row2) {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
    padding-left: 4px;
}

.mc-vp__player :deep(.mc-vp__row2 .plyr__progress__container) {
    flex: 1;
    padding: 0;
}

/* Снимаем плееровские сдвиги под «выпирающий» ползунок — дорожка ровно 100%. */
.mc-vp__player :deep(.plyr__progress) {
    left: 0;
    margin-right: 0;
}

/* Plyr рисует «∕» между временами псевдоэлементом — в нашей раскладке лишний. */
.mc-vp__player :deep(.plyr__time + .plyr__time)::before {
    content: none;
}

.mc-vp__wait {
    position: relative;
    /* Ширину диктует playerBox (аспект + высота) — фикс ломал бы вертикальные. */
    max-width: min(960px, 100%);
    max-height: calc(100vh - 64px);
    overflow: hidden;
    background: #000;
    cursor: default;
}

.mc-vp__wait-img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.mc-vp__progress {
    position: absolute;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 14px;
    background: rgba(0, 0, 0, 0.7);
    color: #fff;
    font-family: var(--mc-mono);
    font-size: 13px;
}

/* Кнопка закрытия — в углу оверлея (вертикальные видео почти не оставляют
   фона, по которому можно кликнуть). */
.mc-vp__close {
    position: absolute;
    top: 16px;
    right: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    background: rgba(0, 0, 0, 0.55);
    border: none;
    color: #fff;
    cursor: pointer;
    transition: color 0.12s ease;
}

.mc-vp__close:hover {
    color: var(--mc-acid);
}

/* ── Анимация открытия/закрытия: фон гаснет, контент чуть масштабируется. ── */
.mc-vp-enter-active,
.mc-vp-leave-active {
    transition: opacity 0.18s ease;
}

.mc-vp-enter-active .mc-vp__player,
.mc-vp-enter-active .mc-vp__wait,
.mc-vp-leave-active .mc-vp__player,
.mc-vp-leave-active .mc-vp__wait {
    transition:
        transform 0.18s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.18s ease;
}

.mc-vp-enter-from,
.mc-vp-leave-to {
    opacity: 0;
}

.mc-vp-enter-from .mc-vp__player,
.mc-vp-enter-from .mc-vp__wait,
.mc-vp-leave-to .mc-vp__player,
.mc-vp-leave-to .mc-vp__wait {
    transform: scale(0.96);
    opacity: 0;
}
</style>
